// payout.repo — akses Supabase pembayaran komisi (commission_payouts).
import { supabase } from '@core/supabase';
import { currentUser } from '@core/session';

export interface Payout {
  id: number; no: string; batch_no: string; emp_nama: string; emp_role: string;
  outlet: string | null; periode: string; amount: number; method: string; catatan: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  requested_by: string; requested_at: string; decided_by: string | null; decided_at: string | null;
  decided_note: string | null; paid_by: string | null; paid_at: string | null; paid_via: string | null;
  expense_id: number | null;
}
const SEL = 'id,no,batch_no,emp_nama,emp_role,outlet,periode,amount,method,catatan,status,requested_by,requested_at,decided_by,decided_at,decided_note,paid_by,paid_at,paid_via,expense_id';

/** Semua payout satu periode (utk tab Bayar Komisi + badge menu Komisi). */
export async function listByPeriode(periode: string): Promise<Payout[]> {
  const { data, error } = await supabase().from('commission_payouts').select(SEL)
    .eq('periode', periode).order('requested_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Payout[];
}

/** Semua payout PENDING (lintas periode) — hub 🛡️ Approval. */
export async function listPendingAll(): Promise<Payout[]> {
  const { data, error } = await supabase().from('commission_payouts').select(SEL)
    .eq('status', 'pending').order('requested_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Payout[];
}

/** Riwayat payout satu orang (semua periode) — dialog 🕘. */
export async function listByEmp(nama: string): Promise<Payout[]> {
  const { data, error } = await supabase().from('commission_payouts').select(SEL)
    .eq('emp_nama', nama).order('requested_at', { ascending: false }).limit(60);
  if (error) throw new Error(error.message);
  return (data ?? []) as Payout[];
}

/** Satu payout by nomor (klik nomor KOM-…) + saudara se-batch (nomor saling terhubung). */
export async function getByNo(no: string): Promise<{ payout: Payout | null; batch: Payout[] }> {
  const { data, error } = await supabase().from('commission_payouts').select(SEL).eq('no', no).maybeSingle();
  if (error) throw new Error(error.message);
  const p = (data as Payout | null) ?? null;
  if (!p) return { payout: null, batch: [] };
  const { data: b, error: e2 } = await supabase().from('commission_payouts').select(SEL)
    .eq('batch_no', p.batch_no).order('emp_nama');
  if (e2) throw new Error(e2.message);
  return { payout: p, batch: (b ?? []) as Payout[] };
}

export interface PayoutInsert {
  no: string; batch_no: string; emp_nama: string; emp_role: string; outlet: string | null;
  periode: string; amount: number; method: string; catatan: string | null;
}
export async function insertBatch(rows: PayoutInsert[]): Promise<void> {
  const { error } = await supabase().from('commission_payouts')
    .insert(rows.map((r) => ({ ...r, requested_by: currentUser()?.nama ?? '' })));
  if (error) throw new Error(error.message);
}

/** Putuskan seluruh batch (approve/reject). .select wajib — update kena 0 baris (RLS/trigger) TIDAK error sendiri. */
export async function decideBatch(batchNo: string, approve: boolean, note?: string): Promise<Payout[]> {
  const { data, error } = await supabase().from('commission_payouts')
    .update({ status: approve ? 'approved' : 'rejected', decided_note: note ?? null })
    .eq('batch_no', batchNo).eq('status', 'pending').select(SEL);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Payout[];
  if (!rows.length) throw new Error('Tidak ada pengajuan pending yang bisa diputus — periksa izin/SoD.');
  return rows;
}

/** Tautkan payout → baris beban yang dibuat saat approve (jejak dua arah). */
export async function linkExpense(id: number, expenseId: number): Promise<void> {
  const { error } = await supabase().from('commission_payouts').update({ expense_id: expenseId }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Beban 'Komisi' utk payout yang disetujui — insert + kembalikan id (dipanggil service saat approve). */
export async function insertKomisiExpense(p: Payout): Promise<number> {
  const { data, error } = await supabase().from('expenses').insert({
    tgl_iso: new Date().toISOString().slice(0, 10), outlet: p.outlet, kategori: 'Komisi',
    nama: `Komisi ${p.emp_nama} ${p.periode} (${p.no})`, jumlah: p.amount,
    by_user: currentUser()?.nama ?? '', catatan: `via ${p.method} · batch ${p.batch_no}`,
  }).select('id').single();
  if (error) throw new Error(error.message);
  return (data as { id: number }).id;
}

/** Eksekusi pembayaran batch approved → paid (Finance isi via; tgl = sekarang). .select wajib (0-baris ≠ error). */
export async function payBatch(batchNo: string, via: string): Promise<Payout[]> {
  const { data, error } = await supabase().from('commission_payouts')
    .update({ status: 'paid', paid_via: via, paid_at: new Date().toISOString() })
    .eq('batch_no', batchNo).eq('status', 'approved').select(SEL);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Payout[];
  if (!rows.length) throw new Error('Tidak ada baris disetujui yang bisa dibayar — periksa izin/status.');
  return rows;
}

/** Payout DIBAYAR pada tanggal tgl (lokal) → uang keluar Tutup Kasir (yang Tunai).
 *  Lewat RPC security-definer (0067): penutup laci bisa CS/SPV, sedangkan RLS read_payout
 *  menyembunyikan payout orang lain dari mereka → tanpa RPC, komisi Tunai lenyap dari
 *  hitungan laci = selisih PALSU yang ikut dibekukan. */
export interface PaidKas { no: string; batch_no: string; emp_nama: string; outlet: string | null; periode: string; amount: number; paid_via: string | null; paid_at: string }
export async function paidForDate(tgl: string, outlet?: string | null): Promise<PaidKas[]> {
  const awal = `${tgl}T00:00:00+07:00`, akhir = `${tgl}T23:59:59.999+07:00`; // TZ toko — sepadan kasir.repo
  const { data, error } = await supabase().rpc('paid_payouts_for_date', { p_awal: awal, p_akhir: akhir });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as PaidKas[];
  return outlet ? rows.filter((r) => (r.outlet ?? '') === outlet) : rows;
}

/** Skema komisi via RPC security-definer — RLS employees membatasi Finance melihat dirinya saja,
 *  padahal payroll butuh skema SEMUA orang. RPC hanya mengekspos kolom skema (bukan wa/rekening). */
export interface KomisiEmp { nama: string; role: string; outlet: string | null; aktif: boolean; komisi_type: string | null; komisi_val: number | null }
export async function fetchKomisiSchemes(): Promise<KomisiEmp[]> {
  const { data, error } = await supabase().rpc('komisi_schemes');
  if (error) throw new Error(error.message);
  return (data ?? []) as KomisiEmp[];
}
